import { createContext, useContext, type ReactNode } from "react";
import { usePageVisibility, useSectionVisibility } from "@/hooks/useSiteSettings";
import { useHomepageConfig } from "@/hooks/useHomepageConfig";
import { pageVisibility as defaultPages, sectionVisibility as defaultSections } from "@/config/pages";

interface SiteSettingsCtx {
  isPageVisible: (path: string) => boolean;
  isSectionVisible: (section: string) => boolean;
  loading: boolean;
}

const SiteSettingsContext = createContext<SiteSettingsCtx>({
  isPageVisible: () => true,
  isSectionVisible: () => true,
  loading: true,
});

export function SiteSettingsProvider({ children }: { children: ReactNode }) {
  const { data: pages, isLoading: pLoading } = usePageVisibility();
  const { data: sections, isLoading: sLoading } = useSectionVisibility();
  const { data: homepageConfig } = useHomepageConfig();

  const pageMap = pages ?? defaultPages;
  const adminSections = sections ?? defaultSections;

  // Merge: Sanity sectionVisibility overrides admin dashboard values (if set)
  const sanitySections = homepageConfig?.sectionVisibility;
  const sectionMap = sanitySections
    ? { ...adminSections, ...sanitySections }
    : adminSections;

  const isPageVisible = (path: string) => pageMap[path] !== false;
  const isSectionVisible = (section: string) => sectionMap[section] !== false;

  return (
    <SiteSettingsContext.Provider value={{ isPageVisible, isSectionVisible, loading: pLoading || sLoading }}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

export function useSiteSettingsContext() {
  return useContext(SiteSettingsContext);
}
